/* eslint-disable import/prefer-default-export */
import i18next from 'i18next';
import t from './Chat.type';
import * as chatService from '../../../services/chat.service';
import { toasterMsg } from '../../../Components/Toaster/Toaster';

const CHAT_NS = 'modules/chat';
const errMsg = () => i18next.t('CHAT.ERROR.OPERATION_FAILED', { ns: CHAT_NS });

/**
 * Fetch chat messages for a room
 * @param {object} params - Parameters including roomName, pagination, filters
 */
export const fetchChatMessages = (params) => {
  return async (dispatch) => {
    try {
      // Don't show global loading for Load More (when isScrolled = true)
      if (!params.isScrolled) {
        dispatch({ type: t.SET_LOADING, payload: true });
      }

      const response = await chatService.getChatMessages(params);

      if (response.code === 200) {
        dispatch({
          type: t.FETCH_CHAT_MESSAGES,
          payload: {
            ...params,
            // Support both messageGroups and messages format
            data: response?.data?.messageGroups || response?.data?.messages || [],
            pagination: response?.data?.pagination, // Pass through pagination data
            totalCount: response?.data?.pagination?.totalCount || response?.data?.totalCount || response?.data?.length || 0,
          },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.FETCH_MESSAGES', { ns: CHAT_NS }),
        });
      }

      // Only hide loading if it was shown
      if (!params.isScrolled) {
        dispatch({ type: t.SET_LOADING, payload: false });
      }
      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      if (!params.isScrolled) {
        dispatch({ type: t.SET_LOADING, payload: false });
      }
      toasterMsg(errMsg, 'danger');
      return null;
    }
  };
};

/**
 * Save a new chat message
 * @param {object} messageData - Message data to save
 */
export const sendMessage = (messageData) => {
  return async (dispatch) => {
    try {
      const response = await chatService.sendMessage(messageData);

      if (response.code === 200) {
        dispatch({
          type: t.SEND_CHAT_MESSAGE,
          payload: response.data,
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: i18next.t(response?.data?.errMsg, { ns: CHAT_NS }),
        });
        toasterMsg(i18next.t(response?.data?.errMsg, { ns: CHAT_NS }), 'danger');
      }
      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      toasterMsg(errMsg, 'danger');
      throw error;
    }
  };
};

/**
 * Edit a chat message
 * @param {string} messageId - Message ID to edit
 * @param {string} message - New message content
 */
export const editChatMessage = (messageData) => {
  return async (dispatch) => {
    try {
      const response = await chatService.editMessage(messageData.serial, messageData);

      if (response.code === 200) {
        dispatch({
          type: t.UPDATE_CHAT_MESSAGE,
          payload: { _id: messageData.serial, message: messageData.message, isEdited: true },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.UPDATE_MESSAGE', { ns: CHAT_NS }),
        });
        toasterMsg(response?.message || i18next.t('CHAT.ERROR.UPDATE_MESSAGE', { ns: CHAT_NS }), 'danger');
      }

      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      toasterMsg(errMsg, 'danger');
      throw error;
    }
  };
};

/**
 * Delete a chat message
 * @param {string} messageId - Message ID to delete
 */
export const deleteChatMessage = (messageData) => {
  return async (dispatch) => {
    try {
      const response = await chatService.deleteMessage(messageData);

      if (response.code === 200) {
        dispatch({
          type: t.DELETE_CHAT_MESSAGE,
          payload: { messageId: messageData.serial },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.DELETE_MESSAGE', { ns: CHAT_NS }),
        });
        toasterMsg(response?.message || i18next.t('CHAT.ERROR.DELETE_MESSAGE', { ns: CHAT_NS }), 'danger');
      }

      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      toasterMsg(errMsg, 'danger');
      throw error;
    }
  };
};

/**
 * Mark messages as read for a room
 * @param {string} roomName - Room name
 * @param {string} module - Module type (e.g., "Samples" or "Costing")
 */
export const markMessagesAsRead = (roomName, moduleRefId) => {
  return async (dispatch) => {
    try {
      const response = await chatService.markMessagesAsRead(roomName, moduleRefId);

      if (response.code === 200) {
        dispatch({
          type: t.MARK_MESSAGES_AS_READ,
          payload: { roomName },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.MARK_READ', { ns: CHAT_NS }),
        });
      }

      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      toasterMsg(i18next.t('CHAT.ERROR.MARK_READ', { ns: CHAT_NS }), 'danger');
      return null;
    }
  };
};

/**
 * Fetch chat participants for a room
 * @param {string} roomName - Room name
 */
export const fetchChatParticipants = (roomName) => {
  return async (dispatch) => {
    try {
      dispatch({ type: t.SET_LOADING, payload: true });

      const response = await chatService.getChatParticipants(roomName);

      if (response.code === 200) {
        dispatch({
          type: t.FETCH_CHAT_PARTICIPANTS,
          payload: {
            data: response?.data?.participants || [],
            totalCount: response?.data?.totalCount || 0,
          },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.FETCH_PARTICIPANTS', { ns: CHAT_NS }),
        });
      }

      dispatch({ type: t.SET_LOADING, payload: false });
      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      dispatch({ type: t.SET_LOADING, payload: false });
      toasterMsg(i18next.t('CHAT.ERROR.FETCH_PARTICIPANTS', { ns: CHAT_NS }), 'danger');
      return null;
    }
  };
};

/**
 * Get unread message count for a module
 * @param {string} moduleRefId - Module reference ID
 * @param {string} module - Module type (e.g., "Samples" or "Costing")
 */
export const fetchUnreadMessageCount = (moduleRefId, module) => {
  return async (dispatch) => {
    try {
      const response = await chatService.getUnreadMessageCount(moduleRefId, module);

      if (response.code === 200) {
        dispatch({
          type: t.FETCH_UNREAD_MESSAGE_COUNT,
          payload: {
            count: response?.data?.count || 0,
            moduleRefId,
          },
        });
      } else {
        dispatch({
          type: t.SET_ERROR,
          payload: response?.message || i18next.t('CHAT.ERROR.FETCH_UNREAD_COUNT', { ns: CHAT_NS }),
        });
      }

      return response;
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      // Don't show toast for unread count errors as they're background operations
      return null;
    }
  };
};

/**
 * Add new message from real-time updates (Ably)
 * @param {object} messageData - Message data from Ably
 */
export const addNewMessage = (messageData) => ({
  type: t.ADD_NEW_MESSAGE,
  payload: messageData,
});

/**
 * Update typing status for users
 * @param {object} typingData - Typing status data
 */
export const updateTypingStatus = (typingData) => ({
  type: t.UPDATE_TYPING_STATUS,
  payload: typingData,
});

/**
 * Set active chat room
 * @param {string} roomName - Room name to set as active
 */
export const setActiveRoom = (roomName) => ({
  type: t.SET_ACTIVE_ROOM,
  payload: roomName,
});

/**
 * Set filter values for chat
 * @param {object} filterData - Filter data
 */
export const setChatFilterValues = (filterData) => ({
  type: t.SET_CHAT_FILTER_DATA,
  payload: filterData,
});

/**
 * Clear chat messages
 */
export const clearChatMessages = () => ({
  type: t.CLEAR_MESSAGES,
});

/**
 * Clear chat error
 */
export const clearChatError = () => ({
  type: t.CLEAR_ERROR,
});

/**
 * Clear entire chat reducer
 */
export const clearChatReducer = () => ({
  type: t.CHAT_CLEAR_REDUCER,
});

/**
 * Fetch users list for mentions (scoped by dev order and module)
 * @param {object} params - { devOrderId, module, mainModule }
 */
export const fetchChatUsersList = (params) => {
  return async (dispatch) => {
    try {
      return await chatService.getUsersList(params);
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      return null;
    }
  };
};

/**
 * Fetch unread messages to prime local UI unread state (no reducer mutation)
 * @param {{ module: string, devOrderId?: string }} params
 */
export const fetchUnreadMessages = (params) => {
  return async (dispatch) => {
    try {
      return await chatService.getUnreadMessages(params);
    } catch (error) {
      dispatch({ type: t.SET_ERROR, payload: error.message });
      return null;
    }
  };
};
